import { SheetPickerList, type SheetPickerOption } from '@/components/global/SheetPickerList';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Label } from '@/components/ui/shadcn/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useTranslations } from '@/hooks/useTranslations';
import { assignableUserId, PRIORITY_ROLES, roleNameOf, type AssignableUser } from '@/lib/employee-roles';
import { router } from '@inertiajs/react';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

type BulkMode = 'assign' | 'unassign';

export interface BulkEmployeesCompany {
    id: string;
    name: string;
    /** Who is on the company today — the unassign list is built from this. */
    users?: Array<{ id: string; first_name?: string; last_name?: string }>;
}

interface Props {
    /** The companies ticked on the index page. */
    companies: BulkEmployeesCompany[];
    /**
     * Already fetched by the page from /api/users/assignable. The same
     * `assign-employees` gate guards both, so re-fetching here would only repeat
     * an identical request.
     */
    employees: AssignableUser[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Fired after a successful save so the page can leave selection mode. */
    onSaved?: () => void;
}

/** Only the badge counts are interpolated client-side; `__()` handles the rest. */
const withCount = (template: string, count: number): string => template.replace(':count', String(count));

/** How many company chips to show before collapsing the rest into a counter. */
const VISIBLE_COMPANY_CHIPS = 8;

/**
 * Adds or removes a set of employees across every company ticked on the index.
 *
 * The direction is picked here rather than on the page: the two actions share the
 * same selection and the same picker, and a neutral entry point keeps nobody from
 * landing in the destructive one by accident.
 */
export default function BulkCompanyEmployeesModal({ companies, employees, open, onOpenChange, onSaved }: Props) {
    const { t } = useTranslations();
    const [mode, setMode] = useState<BulkMode>('assign');
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [processing, setProcessing] = useState(false);

    const isUnassign = mode === 'unassign';

    /**
     * Unassigning may only offer people who are actually on one of the selected
     * companies, and the count tells the user how much of the selection a removal
     * covers. Both come from the rows the page already loaded — no extra request.
     */
    const assignedCounts = useMemo(() => {
        const counts = new Map<string, number>();

        companies.forEach((company) => {
            (company.users ?? []).forEach((user) => {
                counts.set(user.id, (counts.get(user.id) ?? 0) + 1);
            });
        });

        return counts;
    }, [companies]);

    /**
     * Commercial roles first — they are who a company is normally assigned to —
     * then everyone else, each under its own heading.
     *
     * Sorted by name here rather than server-side: first and last names are
     * encrypted at rest, so the endpoint's ORDER BY runs on ciphertext.
     */
    const options = useMemo<SheetPickerOption[]>(() => {
        const suggestedHeading = t('companies.forms.assign_user.suggested', 'Sales & project managers');
        const othersHeading = t('companies.forms.assign_user.others', 'Other colleagues');
        const onCountTemplate = t('companies.bulk_employees.on_count', 'On :count of :total selected');

        const people = employees
            .map((employee) => {
                const id = assignableUserId(employee);

                return {
                    id,
                    label: [employee.first_name, employee.last_name].filter(Boolean).join(' ').trim() || id,
                    role: roleNameOf(employee),
                    assignedTo: assignedCounts.get(id) ?? 0,
                };
            })
            .filter((person) => person.id !== '' && (!isUnassign || person.assignedTo > 0));

        const toOption = (person: (typeof people)[number], group: string): SheetPickerOption => ({
            id: person.id,
            label: person.label,
            keywords: person.role ?? undefined,
            description: isUnassign
                ? onCountTemplate.replace(':count', String(person.assignedTo)).replace(':total', String(companies.length))
                : undefined,
            badge: person.role ? <Badge variant="secondary">{person.role}</Badge> : null,
            group,
        });

        const byLabel = (a: (typeof people)[number], b: (typeof people)[number]) => a.label.localeCompare(b.label);
        const priority = people.filter((person) => PRIORITY_ROLES.includes(person.role ?? '')).sort(byLabel);
        const rest = people.filter((person) => !PRIORITY_ROLES.includes(person.role ?? '')).sort(byLabel);

        return [...priority.map((person) => toOption(person, suggestedHeading)), ...rest.map((person) => toOption(person, othersHeading))];
    }, [employees, assignedCounts, isUnassign, companies.length, t]);

    const reset = () => {
        setMode('assign');
        setSelectedUserIds([]);
        setProcessing(false);
    };

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            reset();
        }
        onOpenChange(next);
    };

    /** The two tabs list different people, so a carried-over selection would lie. */
    const handleModeChange = (next: string) => {
        setMode(next === 'unassign' ? 'unassign' : 'assign');
        setSelectedUserIds([]);
    };

    const handleSubmit = () => {
        setProcessing(true);

        router.post(
            route('companies.bulk-employees'),
            {
                action: mode,
                // From the resolved rows, not the raw id set: that can still hold
                // ids from a page the user has since navigated away from.
                company_uuids: companies.map((company) => company.id),
                user_uuids: selectedUserIds,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    handleOpenChange(false);
                    onSaved?.();
                },
                onError: (errors) => {
                    toast.error(Object.values(errors)[0] ?? t('companies.bulk_employees.errors.failed', 'Could not update the assignments.'));
                },
                // The server flash is toasted by the layout, so there is no
                // success toast here — two would fire.
                onFinish: () => setProcessing(false),
            },
        );
    };

    const visibleCompanies = companies.slice(0, VISIBLE_COMPANY_CHIPS);
    const hiddenCompanyCount = companies.length - visibleCompanies.length;

    const confirmLabel = isUnassign
        ? selectedUserIds.length === 1
            ? t('companies.bulk_employees.confirm_unassign_one', 'Remove 1 employee')
            : withCount(t('companies.bulk_employees.confirm_unassign', 'Remove :count employees'), selectedUserIds.length)
        : selectedUserIds.length === 1
          ? t('companies.bulk_employees.confirm_one', 'Assign 1 employee')
          : withCount(t('companies.bulk_employees.confirm', 'Assign :count employees'), selectedUserIds.length);

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('companies.bulk_employees.title', 'Employees on selected companies')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {isUnassign
                            ? t(
                                  'companies.bulk_employees.description_unassign',
                                  'The employees you pick are removed from every company you selected. Everyone else keeps their assignments.',
                              )
                            : t(
                                  'companies.bulk_employees.description',
                                  'The employees you pick are added to every company you selected. Nobody is removed, and anyone already assigned stays as they are.',
                              )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-5">
                    {/* Tabs drive the direction only — the same picker renders below. */}
                    <Tabs value={mode} onValueChange={handleModeChange}>
                        <TabsList className="w-full">
                            <TabsTrigger value="assign">{t('companies.bulk_employees.mode_assign', 'Assign')}</TabsTrigger>
                            <TabsTrigger value="unassign">{t('companies.bulk_employees.mode_unassign', 'Unassign')}</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <div className="space-y-2">
                        <Label>{t('companies.bulk_employees.companies_label', 'Selected companies')}</Label>
                        <div className="flex flex-wrap gap-1.5">
                            {visibleCompanies.map((company) => (
                                <Badge key={company.id} variant="secondary">
                                    {company.name}
                                </Badge>
                            ))}
                            {hiddenCompanyCount > 0 && (
                                <Badge variant="secondary">
                                    {withCount(t('companies.bulk_employees.companies_more', '+:count more'), hiddenCompanyCount)}
                                </Badge>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>
                            {isUnassign
                                ? t('companies.bulk_employees.employees_label_unassign', 'Employees to remove')
                                : t('companies.bulk_employees.employees_label', 'Employees to add')}
                        </Label>
                        <SheetPickerList
                            // Cancels the negative top margin its sticky search
                            // strip uses to bleed to the body's edges, which
                            // would otherwise ride up over the label above.
                            className="mt-4"
                            multiple
                            values={selectedUserIds}
                            onValuesChange={setSelectedUserIds}
                            options={options}
                            labels={{
                                search: t('companies.forms.assign_user.search', 'Search employees…'),
                                empty: isUnassign
                                    ? t('companies.bulk_employees.empty_assigned', 'None of the selected companies have employees assigned.')
                                    : t('companies.forms.assign_user.empty', 'No employees found.'),
                            }}
                        />
                    </div>

                    {isUnassign && selectedUserIds.length > 0 && (
                        <p className="text-danger-subtle-foreground text-sm">
                            {withCount(
                                t(
                                    'companies.bulk_employees.unassign_warning',
                                    'This removes the selected employees from all :count companies at once.',
                                ),
                                companies.length,
                            )}
                        </p>
                    )}
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={processing}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant={isUnassign ? 'destructive' : 'default'}
                        onClick={handleSubmit}
                        disabled={processing || selectedUserIds.length === 0 || companies.length === 0}
                    >
                        {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {confirmLabel}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
